import { useMemo, useState } from 'react'
import type { ForecastQuestion, StatusCheckResponse } from '../types'
import { saveNewsSummary, statusCheckQuestion } from '../api/client'
import StatusCheckModal from './StatusCheckModal'

function parseEndDate(endDate?: string | null): Date | null {
  if (!endDate) return null
  const s = endDate.trim()
  if (!s) return null
  try {
    // Treat YYYY-MM-DD as end-of-day local time (good enough for UI).
    if (s.length === 10 && s[4] === '-' && s[7] === '-') {
      return new Date(`${s}T23:59:59`)
    }
    return new Date(s)
  } catch {
    return null
  }
}

function formatTimeToResolution(endDt: Date | null): { label: string; isPast: boolean } {
  if (!endDt || Number.isNaN(endDt.getTime())) return { label: 'No resolution date', isPast: false }
  const now = new Date()
  const diffMs = endDt.getTime() - now.getTime()
  if (diffMs <= 0) return { label: 'Past resolution date', isPast: true }
  const hours = Math.floor(diffMs / (1000 * 60 * 60))
  const days = Math.floor(hours / 24)
  if (days >= 365) {
    const years = Math.floor(days / 365)
    const remDays = days % 365
    const yLabel = `${years} ${years === 1 ? 'year' : 'years'}`
    const dLabel = remDays > 0 ? ` ${remDays} ${remDays === 1 ? 'day' : 'days'}` : ''
    return { label: `Resolves in ${yLabel}${dLabel}`, isPast: false }
  }
  if (days >= 2) return { label: `Resolves in ${days}d`, isPast: false }
  return { label: `Resolves in ${hours}h`, isPast: false }
}

export default function ResolutionStatusChip({
  question,
  variant = 'both',
  onSaved,
}: {
  question: ForecastQuestion
  variant?: 'both' | 'label' | 'button'
  onSaved?: () => void
}) {
  const endDt = useMemo(() => parseEndDate(question.end_date), [question.end_date])
  const timeLabel = useMemo(() => formatTimeToResolution(endDt), [endDt])

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<StatusCheckResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const actionLabel = timeLabel.isPast ? 'Check outcome' : 'Latest news about this forecast'
  const loadingLabel = timeLabel.isPast
    ? 'Searching and checking outcome'
    : 'Searching and summarising latest related news'

  const run = async () => {
    setLoading(true)
    setError(null)
    setSaved(false)
    try {
      const res = await statusCheckQuestion(question.question_id, {})
      setData(res)
      setOpen(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch update')
      setOpen(true)
    } finally {
      setLoading(false)
    }
  }

  const save = async () => {
    if (!data || data.mode !== 'news') return
    setSaving(true)
    setError(null)
    try {
      await saveNewsSummary(question.question_id, data)
      setSaved(true)
      onSaved?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save news summary')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div
        className="flex items-center gap-2 flex-wrap"
        onClick={(e) => {
          // Safe embedding in clickable rows later.
          e.stopPropagation()
        }}
      >
        {variant !== 'button' && (
          <span className="text-[11px] text-muted border border-rule rounded-full px-2 py-1 bg-paper">
            {timeLabel.label}
          </span>
        )}

        {variant !== 'label' && (
          <button
            type="button"
            className="rounded-xl px-3 py-2 text-sm font-semibold bg-green text-white shadow-delphi hover:brightness-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={() => void run()}
            disabled={loading}
          >
            {loading ? loadingLabel : actionLabel}
          </button>
        )}
      </div>

      <StatusCheckModal
        open={open}
        onClose={() => setOpen(false)}
        data={data}
        error={error}
        showSaveButton={true}
        onSave={() => void save()}
        saving={saving}
        saved={saved}
      />
    </>
  )
}


